import { useContext, useEffect, useState } from 'react';
import styles from './titularArrendatarioPassoInicial.module.scss';
import { BrLoading, BrSelect } from '@govbr-ds/react-components';
import TitularArrendatarioIdBasica from '../titularArrendatarioIdBasica/titularArrendatarioIdBasica';
import { useBackEndRequests } from '../../lib/backEndRequests';
import { DiefCfemContext } from '../../../../contexts/DiefCfemContext';
import { IDeclaracaoTitularOuArrendatario } from '../../../../interfaces/IDeclaracoes';
import { useQuery } from '@tanstack/react-query';
import ErrorMessage from '../../../../components/errorMessage/errorMessage';
import titularArrendatarioEstadoProcesso from '../../lib/titularArrendatarioEstadoProcesso';
import { IRegimeTributacao } from '../../../../interfaces/IRegimeTributacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface ITitularArrendatarioPassoInicialProps {
  declaracaoId: string;
  declaranteData: any;
  isDeclaranteDataLoading: boolean;
  regimeTributacao: number | null;
  setRegimeTributacao: React.Dispatch<React.SetStateAction<number | null>>;
  setHasDeclaracaoInicial: React.Dispatch<React.SetStateAction<boolean>>;
}

const TitularArrendatarioPassoInicial = ({
  declaracaoId,
  declaranteData,
  isDeclaranteDataLoading,
  setHasDeclaracaoInicial,
  regimeTributacao,
  setRegimeTributacao,
}: ITitularArrendatarioPassoInicialProps) => {
  const { getDeclaracaoById, getRegimesTributacao } = useBackEndRequests();
  const context = useContext(DiefCfemContext);

  const [hasErrorDeclaracaoInicial, setHasErrorDeclaracaoInicial] = useState(false);

  const {
    data: declaracaoTitularArrendatarioData,
    isLoading: isDeclaracaoTitularArrendatarioDataLoading,
    error: declaracaoTitularArrendatarioDataError,
    refetch: refetchDeclaracaoTitularArrendatarioData,
  } = useQuery<IDeclaracaoTitularOuArrendatario, IErrorWithSentryId>({
    queryKey: ['declaracaoTitularArrendatarioQuery', declaracaoId],
    queryFn: async () => await getDeclaracaoById(declaracaoId || ''),
    retryDelay: 100,
  });

  const {
    data: regimesTributacaoData,
    isLoading: isRegimesLoading,
    isError: isRegimesError,
    error: regimesError,
    refetch: refetchRegimesTributacao,
  } = useQuery<IRegimeTributacao[], IErrorWithSentryId>({
    queryKey: ['regimesTributacao', declaranteData?.cpfCnpj],
    queryFn: () => getRegimesTributacao(declaranteData?.cpfCnpj),
    enabled: !!declaranteData?.cpfCnpj,
  });

  useEffect(() => {
    if (!isRegimesLoading && regimesTributacaoData?.length && !regimeTributacao) {
      if (declaranteData?.cpfCnpj?.length === 11) {
        const regimeOutros = regimesTributacaoData.find((regime) => regime.nome.toLowerCase().trim() === 'outros');
        if (regimeOutros) setRegimeTributacao(regimeOutros.id);
      } else if (declaranteData?.cpfCnpj?.length === 14) {
        setRegimeTributacao(null);
      }
    }
  }, [declaranteData?.cpfCnpj, regimesTributacaoData, isRegimesLoading]);

  useEffect(() => {
    titularArrendatarioEstadoProcesso.limparEstado();
    context?.setErrosValidacaoDeclaracao({ tabNContent: [], showContainer: false });
    refetchDeclaracaoTitularArrendatarioData();
  }, []);

  useEffect(() => {
    if (declaracaoTitularArrendatarioData && !isDeclaracaoTitularArrendatarioDataLoading) {
      context?.setDeclaracaoTitularArrendatario(declaracaoTitularArrendatarioData);
      setRegimeTributacao(declaracaoTitularArrendatarioData?.regimeTributacao?.id);
      setHasDeclaracaoInicial(true);
    }
  }, [declaracaoTitularArrendatarioData, isDeclaracaoTitularArrendatarioDataLoading]);

  useEffect(() => {
    if (declaracaoTitularArrendatarioDataError) {
      if (declaracaoTitularArrendatarioDataError?.status === 404) setHasDeclaracaoInicial(false);
      else setHasErrorDeclaracaoInicial(true);
    }
  }, [declaracaoTitularArrendatarioDataError]);

  const content = () => {
    if (isDeclaracaoTitularArrendatarioDataLoading) {
      return (
        <div className={styles.passoInicialContainer}>
          <div className={styles.screenLoading}>
            <BrLoading large label='Carregando...' />
          </div>
        </div>
      );
    }
    if (hasErrorDeclaracaoInicial && !declaracaoTitularArrendatarioData) {
      return (
        <ErrorMessage
          errorTitle='Erro ao carregar declaração'
          errorMessage='Não foi possível carregar a declaração. Por favor, tente novamente mais tarde.'
          errorDescription={declaracaoTitularArrendatarioDataError?.message || ''}
          error={declaracaoTitularArrendatarioDataError}
          onClickFunction={() => refetchDeclaracaoTitularArrendatarioData()}
        />
      );
    }
    return (
      <div className={styles.passoInicialContainer}>
        {isDeclaranteDataLoading ? (
          <div className={styles.loadingContainer}>
            <BrLoading large />
          </div>
        ) : (
          <>
            <TitularArrendatarioIdBasica
              declaranteData={declaranteData}
              isDeclaranteDataLoading={isDeclaranteDataLoading}
              noBorder
            />

            {isRegimesLoading ? (
              <div className={styles.loadingContainer}>
                <BrLoading label='Carregando regimes tributários...' />
              </div>
            ) : isRegimesError ? (
              <ErrorMessage
                errorTitle='Erro ao carregar regimes de tributação'
                errorMessage='Não foi possível carregar a lista de regimes. Tente novamente.'
                errorDescription={regimesError?.message || ''}
                onClickFunction={() => refetchRegimesTributacao()}
              />
            ) : (
              <BrSelect
                data-testid='select-regime-tributacao'
                className={styles.selectRegimeTributacao}
                placeholder='Selecione um regime de tributação...'
                id='select-regime-tributacao'
                label='Regime de Tributação'
                options={
                  regimesTributacaoData?.map((regimesTributario) => ({
                    value: regimesTributario.id,
                    label: regimesTributario.nome,
                  })) || []
                }
                key={regimeTributacao}
                value={regimeTributacao?.toString()}
                onChange={(value: string) => setRegimeTributacao(parseInt(value))}
              />
            )}
          </>
        )}
      </div>
    );
  };

  return content();
};

export default TitularArrendatarioPassoInicial;
